// Render helpers for ProductionHedger: trade ticket block + small cell
// components. Extracted from ProductionHedger.tsx to keep the parent
// component under the 500-line god-file threshold.

interface CollarLeg {
  expiry?: string;
  bid?: number;
  ask?: number;
}

interface TradeTicketProps {
  symbol: string;
  contracts: number;
  callLeg: CollarLeg | null | undefined;
  putLeg: CollarLeg | null | undefined;
  callStrike: number;
  putStrike: number;
  callPriceUnit: number;
  putPriceUnit: number;
  callPremium: number;
  putCost: number;
  netPremium: number;
  hedgeRatio: number;
  shares: number;
  fmtUsd: (n: number) => string;
}

export function TradeTicket({
  symbol, contracts, callLeg, putLeg, callStrike, putStrike,
  callPriceUnit, putPriceUnit, callPremium, putCost, netPremium,
  hedgeRatio, shares, fmtUsd,
}: TradeTicketProps) {
  return (
    <div className="border border-emerald-500/40 rounded p-3 bg-emerald-500/5">
      <div className="text-[9px] uppercase tracking-wider text-emerald-300 mb-2 font-bold">Trade Ticket — orders to place</div>
      <div className="font-mono text-[11.5px] space-y-1.5">
        <div className="grid grid-cols-[44px_60px_1fr_70px_120px_minmax(0,1fr)] items-baseline gap-2 text-[#94a3b8] text-[9px] uppercase tracking-wider">
          <div>Side</div>
          <div className="text-right">Qty</div>
          <div>Contract</div>
          <div className="text-right">Exec @</div>
          <div className="text-right">Bid / Ask</div>
          <div className="text-right">Position $</div>
        </div>
        <div className="grid grid-cols-[44px_60px_1fr_70px_120px_minmax(0,1fr)] items-baseline gap-2 text-[#e2e8f0]">
          <span className="text-emerald-400 font-bold">SELL</span>
          <span className="tabular-nums text-right text-white font-semibold">{contracts.toLocaleString()}</span>
          <span className="whitespace-nowrap overflow-hidden text-ellipsis">{symbol} {callLeg?.expiry ?? ''} ${callStrike} call</span>
          <span className="tabular-nums text-right" title="Sell side: lifted on the BID (what you'd receive)">${callPriceUnit.toFixed(2)}</span>
          <span className="tabular-nums text-right text-[10px] text-[#94a3b8]">${(callLeg?.bid ?? 0).toFixed(2)} / ${(callLeg?.ask ?? 0).toFixed(2)}</span>
          <span className="tabular-nums text-right text-emerald-300 font-semibold">{fmtUsd(callPremium)} credit</span>
        </div>
        <div className="grid grid-cols-[44px_60px_1fr_70px_120px_minmax(0,1fr)] items-baseline gap-2 text-[#e2e8f0]">
          <span className="text-red-400 font-bold">BUY</span>
          <span className="tabular-nums text-right text-white font-semibold">{contracts.toLocaleString()}</span>
          <span className="whitespace-nowrap overflow-hidden text-ellipsis">{symbol} {putLeg?.expiry ?? ''} ${putStrike} put</span>
          <span className="tabular-nums text-right" title="Buy side: pay the ASK (what you'd be charged)">${putPriceUnit.toFixed(2)}</span>
          <span className="tabular-nums text-right text-[10px] text-[#94a3b8]">${(putLeg?.bid ?? 0).toFixed(2)} / ${(putLeg?.ask ?? 0).toFixed(2)}</span>
          <span className="tabular-nums text-right text-red-300 font-semibold">{fmtUsd(-putCost)} debit</span>
        </div>
        <div className="grid grid-cols-[44px_60px_1fr_70px_120px_minmax(0,1fr)] items-baseline gap-2 text-[#e2e8f0] border-t border-emerald-500/20 pt-1.5 mt-1.5">
          <span />
          <span />
          <span className="text-[#94a3b8] text-[10px] uppercase tracking-wider">Net premium</span>
          <span />
          <span />
          <span className={`tabular-nums text-right font-bold text-[14px] ${netPremium >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
            {fmtUsd(netPremium)} {netPremium >= 0 ? 'credit' : 'debit'}
          </span>
        </div>
      </div>
      <div className="text-[10px] text-[#64748b] mt-2 leading-relaxed">
        Each contract covers 100 shares. Execution convention: short legs use the BID (what you receive),
        long legs use the ASK (what you pay) — conservative. The spread column shows the live bid/ask range
        so you can decide whether to leg in via limit orders for a better fill.
        {' '}Hedge ratio {(hedgeRatio * 100).toFixed(0)}% of your {shares.toLocaleString()}-share position
        {hedgeRatio < 1
          ? ` leaves partial long-delta protection.`
          : hedgeRatio === 1
            ? ` — standard 1:1 coverage.`
            : ` over-hedges each leg.`}
      </div>
    </div>
  );
}

export function GreekCell({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: 'green' | 'red' | 'amber' }) {
  const color = tone === 'green' ? 'text-emerald-300' : tone === 'red' ? 'text-red-300' : 'text-amber-300';
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#94a3b8]">{label}</div>
      <div className={`text-[14px] font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="text-[9px] text-[#64748b]">{sub}</div>
    </div>
  );
}

export function SummaryCell({ label, value, tone }: { label: string; value: string; tone: 'green' | 'red' | 'amber' }) {
  const color = tone === 'green' ? 'text-emerald-300' : tone === 'red' ? 'text-red-300' : 'text-amber-300';
  return (
    <div className="bg-[#0a0e14] border border-[#1e293b] rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-[#94a3b8]">{label}</div>
      <div className={`text-[14px] font-semibold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

interface StressCell { spotOffset: number; pnlDollars: number }
interface StressMatrixProps {
  stress: { columns: Array<{ label: string }>; spotOffsets: number[]; cells: StressCell[] };
  fmtUsd: (n: number) => string;
}

export function StressMatrix({ stress, fmtUsd }: StressMatrixProps) {
  return (
    <div className="border border-[#1e293b] rounded p-3 bg-[#0a0e14]">
      <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-2">
        Stress matrix — position P&amp;L in $
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] tabular-nums">
          <thead className="text-[#64748b] uppercase tracking-wider text-[8px]">
            <tr>
              <th className="text-left">Spot</th>
              {stress.columns.map((c, i) => <th key={i} className="text-right pl-3">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {stress.spotOffsets.map(off => {
              const row = stress.cells.filter(c => c.spotOffset === off);
              return (
                <tr key={off} className={off === 0 ? 'bg-amber-500/5' : ''}>
                  <td className="text-[#cbd5e1]">{off === 0 ? 'now' : (off > 0 ? '+' : '') + (off * 100).toFixed(0) + '%'}</td>
                  {row.map((c, i) => (
                    <td key={i} className={`text-right pl-3 ${c.pnlDollars > 0 ? 'text-emerald-300' : c.pnlDollars < 0 ? 'text-red-300' : 'text-[#94a3b8]'}`}>
                      {fmtUsd(c.pnlDollars)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
